import { validateCanonicalDataset } from '@canonical-process';

export const INGESTION_SOURCE_TYPES = Object.freeze({
  JIRA_FORGE: 'jira-forge',
  SERVICENOW_APP: 'servicenow-app',
  JIRA_REST: 'jira-rest',
  SERVICENOW_REST: 'servicenow-rest',
  CSV_EVENT_LOG: 'csv-event-log',
  CSV_WORK_ITEMS: 'csv-work-items',
});

export function buildDatasetDiagnostics(dataset, sourceDiagnostics = []) {
  const diagnostics = [...(sourceDiagnostics || [])];
  const validation = validateCanonicalDataset(dataset);
  validation.errors.forEach(message => diagnostics.push({ severity: 'error', code: 'canonical-validation', message }));

  const cases = dataset?.cases || [];
  const events = dataset?.events || [];
  const eventIds = new Set();
  const caseIds = new Set(cases.map(c => String(c.id)));
  const caseEventCounts = new Map();

  for (const event of events) {
    const id = String(event?.id || '');
    if (id && eventIds.has(id)) diagnostics.push({ severity: 'error', code: 'duplicate-event-id', message: `Duplicate event id: ${id}` });
    if (id) eventIds.add(id);
    const caseId = String(event?.caseId || '');
    if (caseId) caseEventCounts.set(caseId, (caseEventCounts.get(caseId) || 0) + 1);
    if (event?.timestamp && Number.isNaN(Date.parse(String(event.timestamp).replace(' ', 'T')))) {
      diagnostics.push({ severity: 'warning', code: 'invalid-timestamp', message: `Event ${id || '(unknown)'} has an unparseable timestamp: ${event.timestamp}` });
    }
  }

  for (const c of cases) {
    const caseId = String(c?.id || '');
    if (caseId && !caseEventCounts.has(caseId)) diagnostics.push({ severity: 'warning', code: 'case-without-events', message: `Case ${c?.key || caseId} has no events` });
  }

  const counts = diagnostics.reduce((acc, d) => {
    const key = d?.severity || 'info';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, { error: 0, warning: 0, info: 0 });

  return {
    valid: counts.error === 0 && validation.valid,
    counts,
    diagnostics,
    summary: {
      cases: cases.length,
      events: events.length,
      caseAttributes: dataset?.attributeDefinitions?.case?.length || 0,
      eventAttributes: dataset?.attributeDefinitions?.event?.length || 0,
      sourceType: dataset?.source?.type || dataset?.source?.platform || '',
    },
  };
}

export function createIngestionResult({ sourceType, dataset, diagnostics = [], metadata = {} }) {
  const enrichedDataset = {
    ...dataset,
    source: {
      ...(dataset?.source || {}),
      type: sourceType,
    },
    metadata: {
      ...(dataset?.metadata || {}),
      ingestion: {
        sourceType,
        ingestedAt: new Date().toISOString(),
        ...metadata,
      },
    },
  };
  const validation = buildDatasetDiagnostics(enrichedDataset, diagnostics);
  return { sourceType, dataset: enrichedDataset, validation, metadata };
}

export function createAssessmentRuntimeInput(ingestionResult) {
  if (!ingestionResult?.dataset) throw new Error('An ingestion result with a canonical dataset is required');
  if (!ingestionResult?.validation?.valid) throw new Error('Cannot run assessment: canonical dataset contains validation errors');
  return {
    canonicalDataset: ingestionResult.dataset,
    sourceType: ingestionResult.sourceType,
    diagnostics: ingestionResult.validation,
    runtimeMetadata: {
      createdAt: new Date().toISOString(),
      ingestion: ingestionResult.metadata || {},
    },
  };
}

export * from './sourceAdapters.js';
